import path from 'path'
import fs from 'fs/promises'
import { existsSync } from 'fs'
import { Client } from '@botpress/client'
import { auth } from '../auth/index.js'
import { AgentProject } from '../agent-project/index.js'

export interface ProjectCache {
  devId?: string
  botId?: string
}

export class DevIdManager {
  private projectPath: string
  private botProjectPath: string
  private projectCachePath: string
  private client?: Client

  constructor(projectPath: string, botProjectPath: string) {
    this.projectPath = projectPath
    this.botProjectPath = botProjectPath
    this.projectCachePath = path.join(botProjectPath, '.botpress', 'project.cache.json')
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()
      const project = await this.getProject()

      // Use workspaceId from agent.json if available, otherwise fall back to profile
      const workspaceId = project.agentInfo?.workspaceId || credentials.workspaceId

      if (!workspaceId) {
        throw new Error('No workspace ID found in agent.json or current profile. Please login again with "adk login"')
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId: workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  async getProject(): Promise<AgentProject> {
    return await AgentProject.load(this.projectPath)
  }

  async readProjectCache(): Promise<ProjectCache> {
    try {
      if (existsSync(this.projectCachePath)) {
        const content = await fs.readFile(this.projectCachePath, 'utf-8')
        return JSON.parse(content)
      }
    } catch (error) {
      console.error('Error reading project.cache.json:', error)
    }
    return {}
  }

  async saveProjectCache(cache: ProjectCache): Promise<void> {
    try {
      // Ensure directory exists
      await fs.mkdir(path.dirname(this.projectCachePath), { recursive: true })

      await fs.writeFile(this.projectCachePath, JSON.stringify(cache, null, 2))
    } catch (error) {
      console.error('Error saving project.cache.json:', error)
    }
  }

  async preserveDevId(): Promise<void> {
    // Read devId from project cache after bp dev creates it
    const projectCache = await this.readProjectCache()
    if (projectCache.devId) {
      // Save it to agent.json using AgentProject methods
      const project = await this.getProject()

      if (project.agentInfo) {
        // Update existing agent info
        await project.updateAgentInfo({
          devId: projectCache.devId,
        })
      } else {
        // Create new agent info
        await project.createAgentInfo({
          botId: projectCache.botId || '',
          workspaceId: '',
          devId: projectCache.devId,
        })
      }
    }
  }

  async restoreDevId(): Promise<void> {
    // Read from agent.json using AgentProject
    const project = await this.getProject()
    const agentInfo = project.agentInfo

    if (agentInfo?.devId || agentInfo?.botId) {
      // Create project cache with the saved IDs
      const cache: ProjectCache = {}

      if (agentInfo.devId) {
        cache.devId = agentInfo.devId
      }

      if (agentInfo.botId) {
        cache.botId = agentInfo.botId
      }

      await this.saveProjectCache(cache)
    }
  }

  async checkDevBotExists(): Promise<boolean> {
    const project = await this.getProject()
    const agentInfo = project.agentInfo

    // If we have a devId, check if the bot still exists
    if (agentInfo?.devId) {
      try {
        const client = await this.getClient()
        await client.getBot({ id: agentInfo.devId })
        // Bot exists
        return true
      } catch (error) {
        // Bot doesn't exist or error getting it
        return false
      }
    }

    // No devId set
    return false
  }
}
